import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import moment from "moment";
import {
    fetchHelpSupportList,
    deleteHelpSupport,
    deleteMultipleHelpSupport,
} from "../../components/services/helpSupportService";
import { getModulePermissions } from "../../utils/permissionUtils";
import { showConfirmationDialog } from "../../utils/apiHelper";

const HelpSupportListPage = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedItems, setSelectedItems] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [search, setSearch] = useState("");

    const navigate = useNavigate();
    const { shouldCheckPermissions, canView, canDelete } =
        getModulePermissions("help-support");

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, itemsPerPage, search]);

    const fetchData = async (page) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: itemsPerPage,
                search,
            };

            const res = await fetchHelpSupportList(params);

            setRequests(res?.data?.data || []);
            setTotalItems(res?.data?.pagination?.total || 0);
            setTotalPages(res?.data?.pagination?.totalPages || 1);
        } catch (err) {
            setErrorMessage(err.message || "Failed to fetch help & support data");
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setLoading(false);
        }
    };

    /* Checkbox logic */
    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedItems(requests.map((r) => r._id));
        else setSelectedItems([]);
    };

    const handleSelectItem = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const isSelected = (id) => selectedItems.includes(id);

    /* Pagination */
    const handleNext = () =>
        currentPage < totalPages && setCurrentPage((p) => p + 1);

    const handlePrev = () =>
        currentPage > 1 && setCurrentPage((p) => p - 1);

    const getPaginationRange = () => {
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };

    /* Delete */
    const handleDelete = async (id, single = "No") => {
        const idsToDelete = single === "Yes" ? [id] : selectedItems;

        if (!idsToDelete || idsToDelete.length === 0) return;

        if (idsToDelete.length === 1) single = "Yes";

        const result = await showConfirmationDialog(
            "This action will delete the help & support record!",
            "Yes, delete it!"
        );

        if (!result.isConfirmed) return;

        try {
            if (single === "Yes") {
                await deleteHelpSupport(idsToDelete[0]);
            } else {
                await deleteMultipleHelpSupport(idsToDelete);
                setSelectedItems([]);
            }

            setSuccessMessage(
                single === "Yes"
                    ? "Record deleted successfully!"
                    : "Records deleted successfully!"
            );

            setTimeout(() => setSuccessMessage(""), 3000);
            fetchData(currentPage);
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message || "Delete failed"
            );
            setTimeout(() => setErrorMessage(""), 3000);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            {successMessage && (
                <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
            )}
            {errorMessage && (
                <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
            )}

            {/* Header */}
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">
                        Show
                    </span>

                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{ fontSize: "16px", borderRadius: "7px" }}
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(parseInt(e.target.value, 10));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10 items per page</option>
                        <option value={20}>20 items per page</option>
                        <option value={50}>50 items per page</option>
                        <option value={100}>100 items per page</option>
                    </select>

                    <form className="navbar-search">
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            name="search"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                            onKeyUp={(e) => {
                                if (e.target.value === "") {
                                    setCurrentPage(1);
                                    //window.location.reload(); // Reload page only when search is cleared
                                }
                            }}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                        {search && (
                            <button
                                onClick={() => {
                                    setSearch("");
                                    setCurrentPage(1);
                                    // Reset your data here if needed
                                }}
                                style={{
                                    position: "absolute",
                                    insetInlineEnd: "1.9375rem",
                                    top: "40%",
                                    transform: "translateY(-50%)",
                                    fontSize: "1.125rem",
                                    color: "var(--black)", // Corrected from `color: var(--black)` which was outside the object
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                }}
                            >
                                <Icon icon="ic:round-close" />
                            </button>
                        )}
                    </form>
                    {(!shouldCheckPermissions || canDelete) && (
                        <button
                            className="btn btn-danger"
                            onClick={() => handleDelete(null, "No")}
                            disabled={selectedItems.length === 0}
                        >
                            Delete All
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                {(!shouldCheckPermissions || canDelete) && (
                                    <th scope="col">
                                        <div className="d-flex align-items-center gap-10">
                                            <div className="form-check style-check d-flex align-items-center">
                                                <input
                                                    className="form-check-input radius-4 border input-form-dark"
                                                    type="checkbox"
                                                    name="checkbox"
                                                    id="selectAll"
                                                    checked={
                                                        requests.length > 0 &&
                                                        selectedItems.length === requests.length
                                                    }
                                                    onChange={handleSelectAll}
                                                />
                                            </div>
                                        </div>
                                    </th>
                                )}
                                <th>Full Name</th>
                                <th>Email</th>
                                {/* <th>Status</th> */}
                                <th>App Version</th>
                                <th>PlatForm</th>
                                <th>Created At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        <span className="loader-15"></span>
                                    </td>
                                </tr>
                            ) : requests.length > 0 ? (
                                requests.map((item) => (
                                    <tr key={item._id}>


                                        <td>
                                            <div className="d-flex align-items-center gap-10">
                                                <div className="form-check style-check d-flex align-items-center">
                                                    <input
                                                        className="form-check-input radius-4 border border-neutral-400"
                                                        type="checkbox"
                                                        name="checkbox"
                                                        value={item._id}
                                                        checked={isSelected(item._id)}
                                                        onChange={() => handleSelectItem(item._id)}
                                                    />
                                                </div>
                                            </div>
                                        </td>

                                        <td>{item.fullName}</td>
                                        <td>{item.email}</td>
                                        {/* <td>
                                            <span>
                                                {item.status}
                                            </span>
                                        </td> */}
                                        <td>
                                            {item.appVersion ? item.appVersion : "-"}
                                        </td>
                                        <td>{item?.platform ? item?.platform : "-"}</td>
                                        <td>{moment(item.createdAt).format("DD-MM-YYYY")}</td>
                                        <td className="d-flex gap-2">
                                            {(!shouldCheckPermissions || canView) && (
                                                <button
                                                    title="View"
                                                    onClick={() =>
                                                        navigate(`/help-support/view/${item._id}`)
                                                    }
                                                    className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon icon="majesticons:eye-line" />
                                                </button>
                                            )}

                                            {(!shouldCheckPermissions || canDelete) && (
                                                <button
                                                    title="Delete"
                                                    onClick={() => handleDelete(item._id, "Yes")}
                                                    className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                >
                                                    <Icon icon="fluent:delete-24-regular" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No Data Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                    <span>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                        {totalItems} entries
                    </span>

                    <ul className="pagination d-flex flex-wrap align-items-center gap-2">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#" onClick={handlePrev}>
                                <Icon icon="ep:d-arrow-left" />
                            </Link>
                        </li>

                        {getPaginationRange().map((page) => (
                            <li key={page} className="page-item">
                                <Link

                                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                                        ? "bg-primary-600 text-white"
                                        : "bg-neutral-200"
                                        }`}
                                    to="#"
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </Link>
                            </li>
                        ))}

                        <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                }`}

                        >
                            <Link className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#" onClick={handleNext}>
                                <Icon icon="ep:d-arrow-right" />
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default HelpSupportListPage;
